import {
    Avatar,
    classNames,
    IconNames,
    layout,
    MaterialIcon,
    PushButton,
    typography,
    useAsync
} from "@variocube/cube-ui";
import * as QRCode from "qrcode";
import * as React from "react";
import {style, stylesheet} from "typestyle";
import {grid} from "../themes";
import {ShopHomeViewProps} from "../types";
import {Panel} from "../Panel";
import {useLocalization} from "../../i18n";

export function GenericShopHomeView({shop, onBack}: ShopHomeViewProps) {
    const {t} = useLocalization();
    const {phone, email, app, url} = shop.orderOptions;

    const appQr = useAsync(async () => app && QRCode.toDataURL(app, {margin: 0}), [app]);
    const urlQr = useAsync(async () => url && QRCode.toDataURL(url, {margin: 0}), [url]);

    return (
        <div className={classNames(layout.vertical, styles.home)}>
            {onBack && (
                <div className={styles.backContainer}>
                    <PushButton onClick={onBack} icon={MaterialIcon.arrow_back}>
                        {t("Back")}
                    </PushButton>
                </div>
            )}

            <Panel>
                <div className={classNames(layout.horizontal, grid.container)}>
                    <div className={grid.item}>
                        <div className={typography.title}>{shop.name}</div>
                        <p>{shop.offer.longDescription}</p>
                    </div>
                    <div className={grid.item}>
                        <div className={styles.logoContainer}>
                            <img className={styles.logo} src={shop.logoUrl} alt="Logo"/>
                        </div>
                    </div>
                </div>

                <div className={classNames(layout.horizontalWrap, grid.container)}>
                    <div className={classNames(grid.item, style({flex: "0 1 100%"}))}>
                        <div className={styles.orderOptions}>{t("OrderOptions")}</div>
                    </div>
                    {app && (
                        <OrderOption title={t("App")} icon={<MaterialIcon icon={IconNames.smartphone}/>}
                                     value={app}/>
                    )}
                    {url && (
                        <OrderOption title={t("Online")} icon={<MaterialIcon icon={IconNames.web}/>}
                                     value={url}/>
                    )}
                    {phone && (
                        <OrderOption title={t("Phone")} icon={<MaterialIcon icon={IconNames.call}/>}
                                     value={phone}/>
                    )}
                    {email && (
                        <OrderOption title={t("Email")} icon={<MaterialIcon icon={IconNames.email}/>}
                                     value={email}/>
                    )}
                </div>
            </Panel>

            <div className={classNames(layout.fill, layout.horizontal, layout.alignStart)}>
                {appQr.result && (
                    <Panel>
                        <div className={classNames(grid.container, layout.vertical, layout.alignCenter)}>
                            <div className={grid.item}>
                                App
                            </div>
                            <div className={grid.item}>
                                <img src={appQr.result} alt="App QR-Code"/>
                            </div>
                        </div>
                    </Panel>
                )}
                {urlQr.result && (
                    <Panel>
                        <div className={classNames(grid.container, layout.vertical, layout.alignCenter)}>
                            <div className={grid.item}>
                                Online
                            </div>
                            <div className={grid.item}>
                                <img src={urlQr.result} alt="Online QR-Code"/>
                            </div>
                        </div>
                    </Panel>
                )}
            </div>
        </div>
    );
}

function OrderOption({title, icon, value}: { title: string; icon: React.ReactNode; value: string; }) {
    return (
        <div className={classNames(grid.item, layout.fill)}>
            <div className={classNames(layout.horizontal, layout.alignCenter, grid.container, grid.nested)}>
                <div className={classNames(grid.item)}>
                    <div className={styles.orderOptionIcon}>
                        <Avatar primary>{icon}</Avatar>
                    </div>
                </div>
                <div className={classNames(grid.item, layout.fill, styles.orderOptionText)}>
                    <div className={styles.orderOptionTitle}>
                        {title}
                    </div>
                    <div className={styles.orderOptionValue}>
                        {value}
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = stylesheet({
    home: {
        padding: "0.75em",
        background: "transparent",
        height: "100%",
        overflowY: "auto",
    },
    backContainer: {
        padding: "0 0.75em"
    },
    logoContainer: {
        width: "16em",
        minWidth: "12em",
        height: "16em",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },
    logo: {
        width: "100%",
        height: "auto",
        maxHeight: "100%",
        maxWidth: "100%"
    },
    orderOptions: {
        opacity: 0.75,
        letterSpacing: "0.125em",
        textTransform: "uppercase",
        textAlign: "left",
        margin: "0.75em 0 0.25em 0",
    },
    orderOptionIcon: {
        fontSize: "2em",
        lineHeight: 1,
    },
    orderOptionText: {},
    orderOptionTitle: {
        fontWeight: 500,
        fontSize: "0.875em",
    },
    orderOptionValue: {},

});
